import React, { useEffect, useState } from "react";
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  Badge,
  HStack,
  IconButton,
  Spinner,
  Flex,
} from "@chakra-ui/react";
import { Project } from "./ProjectPerformance";
import { FaArrowLeft, FaEye, FaHashtag, FaLayerGroup, FaInfoCircle } from "react-icons/fa";
import ChannelDetails from "./ChannelDetails";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../firebase/firebase";
import ProjectAnalytics from "./ProjectAnalytics";
import { assign } from "nodemailer/lib/shared";
import TaskOverviewChart from "../../Gantt/TaskOverviewChart";
import TaskCompletionChart from "../../Gantt/TaskCompletionChart";
import RiskManagementChart from "../../Gantt/RiskManagementChart";
import { IconWrapper } from "../../../components/IconWrapper";
import { useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../../utils/themeUtils";
import { getProjectScopeAccess } from "../../../utils/permissions";

interface Channel {
  id: string; // Firestore Document ID
  name: string; // Channel name
  assignedUsers: string[];
}

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  phone: string;
  assignedProjects?: string[];
  assignedTasks?: { id: string; name: string }[];
  organizationId: string;
};

interface TaskData {
  id: string;
  name: string;
  status: string;
  subtasks: TaskData[];
  risk?: string;
}

interface Task {
  id: string;
  title: string;
  description: string;
  data: TaskData[];
}

interface ProjectDetailsProps {
  project: Project;
  onBack: () => void;
  organizationId: string;
  color: string;
  bg: string;
  users: User[];
  isAdmin: boolean;
  currentUser?: any;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({
  project,
  onBack,
  organizationId,
  color,
  bg,
  users,
  isAdmin,
  currentUser,
}) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [loading, setLoading] = useState(true);
  const [channelPage, setChannelPage] = useState(1);
  const channelsPerPage = 4;
  const totalChannelPages = Math.ceil(channels.length / channelsPerPage);
  const paginatedChannels = channels.slice(
    (channelPage - 1) * channelsPerPage,
    channelPage * channelsPerPage
  );

  // AGGREGATE ALL TASKS AND SUBTASKS ACROSS ALL CHANNELS
  const [taskStats, setTaskStats] = useState({
    delayed: 0,
    notStarted: 0,
    completed: 0,
    inProgress: 0,
    lowRisk: 0,
    mediumRisk: 0,
    highRisk: 0,
    total: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const channelRef = collection(
          db,
          `organization/${organizationId}/projects/${project.id}/channels`
        );
        const channelSnapshot = await getDocs(channelRef);
        const fetchedChannels: Channel[] = [];

        let delayed = 0,
          notStarted = 0,
          completed = 0,
          inProgress = 0;
        let lowRisk = 0,
          mediumRisk = 0,
          highRisk = 0;
        let total = 0;

        for (const doc of channelSnapshot.docs) {
          const channelId = doc.id;
          const channelData = doc.data();
          const assignedUsers = channelData.assignedUsers || [];

          fetchedChannels.push({
            id: channelId,
            name: channelData.name || "Unnamed Channel",
            assignedUsers,
          });

          const tasksRef = collection(
            db,
            `organization/${organizationId}/projects/${project.id}/channels/${channelId}/tasks`
          );
          const tasksSnap = await getDocs(tasksRef);

          for (const taskDoc of tasksSnap.docs) {
            const taskData = taskDoc.data();
            const taskEntries: TaskData[] = taskData.data || [];
            console.log("taskEntries", taskEntries);

            for (const entry of taskEntries) {
              // Status
              switch ((entry.status || "").toLowerCase()) {
                case "not-started":
                  notStarted++;
                  break;
                case "started":
                  inProgress++;
                  break;
                case "in-progress":
                  inProgress++;
                  break;
                case "completed":
                  completed++;
                  break;
                case "delay":
                  delayed++;
                  break;
                default:
                  break;
              }
              // Risk (if available)
              if (entry.risk) {
                switch ((entry.risk || "").toLowerCase()) {
                  case "low":
                    lowRisk++;
                    break;
                  case "medium":
                    mediumRisk++;
                    break;
                  case "high":
                    highRisk++;
                    break;
                  default:
                    break;
                }
              }
              total++;
              // Subtasks
              const subtasks: TaskData[] = entry.subtasks || [];
              for (const subtask of subtasks) {
                switch ((subtask.status || "").toLowerCase()) {
                  case "not-started":
                    notStarted++;
                    break;
                  case "started":
                    inProgress++;
                    break;
                  case "in-progress":
                    inProgress++;
                    break;
                  case "completed":
                    completed++;
                    break;
                  case "delay":
                    delayed++;
                    break;
                  default:
                    break;
                }
                if (subtask.risk) {
                  switch ((subtask.risk || "").toLowerCase()) {
                    case "low":
                      lowRisk++;
                      break;
                    case "medium":
                      mediumRisk++;
                      break;
                    case "high":
                      highRisk++;
                      break;
                    default:
                      break;
                  }
                }
                total++;
              }
            }
          }
        }
        // Admin-level access on this project (org-wide or granted per project)
        // sees every channel; editors and viewers see only assigned ones.
        const projectScope = getProjectScopeAccess(currentUser, project.id);
        const visibleChannels = projectScope.canViewAllChannels
          ? fetchedChannels
          : fetchedChannels.filter((ch) => {
              const assignedChannelIds = currentUser?.assignedChannels?.[project.id] || [];
              return assignedChannelIds.includes(ch.id);
            });
        setChannels(visibleChannels);
        setTaskStats({
          delayed,
          notStarted,
          completed,
          inProgress,
          lowRisk,
          mediumRisk,
          highRisk,
          total,
        });
      } catch (error) {
        console.error("Error loading project/channel data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [organizationId, project.id]);

  return (
    <Box p={5} bg={bg}>
      {!selectedChannel ? (
        <>
          {/* Top row: Back arrow on left, Project name on right */}
          <Flex justify="space-between" align="center" mb={4}>
            <Button
              leftIcon={<IconWrapper icon={FaArrowLeft} size={16} />}
              onClick={onBack}
              variant="gold"
              size="xs"
            >
              Back
            </Button>
            <Heading fontSize="lg" color={color}>
              {project.name}
            </Heading>
          </Flex>

          <Flex gap={6} direction={{ base: "column", lg: "row" }}>
            {/* LEFT SIDE - Channel List */}
            <Box w={{ base: "100%", lg: "30%" }}>

              {/* Project Stats Header */}
              <div className={`mb-4 p-3 rounded-lg border ${theme.border} ${theme.card} ${isDark ? 'bg-[linear-gradient(90deg,rgba(181,145,67,0.12),rgba(181,145,67,0))]' : 'bg-gradient-to-r from-[#FFF7E6] to-white'}`}>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center p-2">
                    <div className="text-2xl font-bold text-[#B59143]">
                      {channels.length}
                    </div>
                    <div className={`text-xs ${theme.textMuted}`}>Total Channels</div>
                  </div>
                  <div className="text-center p-2">
                    <div className="text-2xl font-bold text-[#B59143]">
                      {taskStats.total}
                    </div>
                    <div className={`text-xs ${theme.textMuted}`}>Total Tasks</div>
                  </div>
                </div>
              </div>

              <div className={`rounded-lg p-3 ${isDark ? 'bg-[linear-gradient(90deg,rgba(181,145,67,0.12),rgba(181,145,67,0))] border border-white/20' : 'bg-[#F8FAFC] border border-gray-200'}`} style={{ minHeight: "400px" }}>
                <Text fontWeight="semibold" color={color} fontSize="sm" mb={2}>
                  Channel List
                </Text>

                {loading ? (
                  <div className="flex justify-center items-center h-20">
                    <svg
                      className="animate-spin h-5 w-5 text-[#B59143]"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v8z"
                      ></path>
                    </svg>
                  </div>
                ) : channels.length > 0 ? (
                  <>
                    <div className="grid grid-cols-1 gap-2">
                      {paginatedChannels.map((channel) => (
                        <div
                          key={channel.id}
                          className={`group relative overflow-hidden rounded-md shadow-sm hover:shadow-sm transition-all duration-200 ${isDark ? 'bg-[#1A1C1F] border border-[#2E2F33]' : 'bg-white border border-gray-100'} hover:border-[#B59143]`}
                        >
                          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-[#FBD282] to-[#B59143]"></div>
                          <div className="flex justify-between items-center p-2.5 pl-4">
                            <div className="flex items-center space-x-3">
                              <div className="relative">
                                <div className={`w-8 h-8 rounded-full ${isDark ? 'bg-transparent border border-[#B59143]' : 'bg-[#FFF7E6] border border-[#E8C16C]'} flex items-center justify-center`}>
                                  <span className={`text-sm font-semibold ${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'}`}>
                                    {channel.name.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div>
                                <div className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                  {channel.name}
                                </div>
                                <div className={`text-xs ${theme.textMuted}`}>
                                  {channel.assignedUsers?.length || 0} members
                                </div>
                              </div>
                            </div>
                            <button
                              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors duration-200 group-hover:shadow-sm border ${isDark ? 'border-[#B59143] text-white hover:bg-[#2A3441]' : 'border-[#B59143] text-[#B59143] hover:bg-[#FFF7E6]'}`}
                              onClick={() => setSelectedChannel(channel)}
                            >
                              View Tasks Group
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center mt-3">
                      <span className={`text-xs ${theme.textMuted}`}>
                        Page {channelPage} of {totalChannelPages}
                      </span>
                      <div className="flex space-x-1">
                        <button
                          className={`px-2.5 py-1 text-xs font-medium rounded border ${isDark ? 'text-white bg-transparent border-[#B59143] hover:bg-[#2A3441]' : 'text-[#B59143] bg-white border-[#B59143] hover:bg-[#FFF7E6]'} disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200`}
                          onClick={() => setChannelPage(channelPage - 1)}
                          disabled={channelPage === 1}
                        >
                          Previous
                        </button>
                        <button
                          className={`px-2.5 py-1 text-xs font-medium rounded border ${isDark ? 'text-white bg-transparent border-[#B59143] hover:bg-[#2A3441]' : 'text-[#B59143] bg-white border-[#B59143] hover:bg-[#FFF7E6]'} disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200`}
                          onClick={() => setChannelPage(channelPage + 1)}
                          disabled={channelPage === totalChannelPages}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className={`${theme.textMuted} text-sm italic`}>
                    No channels found.
                  </div>
                )}
              </div>
            </Box>

            <Box w={{ base: "100%", lg: "70%" }} bg={bg}>

              <Box mb={4} textAlign="center">
                <div className={`p-3 rounded-lg border ${isDark ? 'border-[#2E2F33] bg-[#1A1C1F] bg-[linear-gradient(90deg,rgba(181,145,67,0.12),rgba(181,145,67,0))]' : 'border-gray-200 bg-gradient-to-r from-[#FFF7E6] to-white'}`} style={{ minHeight: "90px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Text
                    fontSize="xl"
                    fontWeight="bold"
                    color={isDark ? 'white' : 'gray.800'}
                    mb={0}
                  >
                    Project Analytical Overview
                  </Text>
                </div>
              </Box>

              <div className={`w-full p-3 rounded-md ${isDark ? 'bg-[#1A1C1F] border border-[white/20]' : 'bg-white border border-gray-300'}`} style={{ minHeight: "396px", marginTop: "18px" }}>
                <HStack spacing={2} mb={4} justify="center" w="100%">
                  <IconWrapper
                    icon={FaInfoCircle}
                    className={`${isDark ? 'text-[#C3C9D3]' : 'text-gray-400'}`}
                    size={16}
                  />
                  <Text
                    fontWeight="semibold"
                    fontSize="sm"
                    color={isDark ? 'gray.300' : 'gray.500'}
                    textAlign="center"
                  >
                    Task Overview
                  </Text>
                </HStack>
                <Box height="252px" width="100%" className="flex items-center justify-start pl-0" style={{ marginTop: "54px" }}>
                  <TaskOverviewChart
                    delayedTasks={taskStats.delayed}
                    notStartedTasks={taskStats.notStarted}
                    completedTasks={taskStats.completed}
                    inProgressTasks={taskStats.inProgress}
                  />
                </Box>
              </div>
            </Box>
          </Flex>

          {/* Task Overview and Task Progress Monitoring Section - Full Width Below Channel List and Risk Management */}
          <Box
            w="100%"
            mt={5}
            p={5}
            bg={bg}
            borderRadius="lg"
            boxShadow="sm"

          >
            {/* Charts Section */}
            <Flex
              direction={{ base: "column", md: "row" }}
              gap={5}
              px={0}
              pb={0}
            >
              {/* LEFT – Risk Management Chart */}
              <Box
                flex="1"
                minH="300px"
                border="1px solid"
                borderColor={isDark ? 'rgba(255, 255, 255, 0.2)' : 'gray.300'}
                borderRadius="lg"
                p={4}
                bg={isDark ? '#1A1C1F' : 'white'}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontWeight="semibold"
                  color={isDark ? 'gray.300' : 'gray.500'}
                  fontSize="sm"
                  mb={10}
                  mt={0}
                  textAlign="center"
                >
                  Project Risk Management
                </Text>
                <RiskManagementChart
                  lowRiskTasks={taskStats.lowRisk}
                  mediumRiskTasks={taskStats.mediumRisk}
                  highRiskTasks={taskStats.highRisk}
                  totalTasks={taskStats.total}
                />
              </Box>

              {/* RIGHT – Task Progress Monitoring Donut Chart */}
              <Box
                flex="1"
                minH="300px"
                border="1px solid"
                borderColor={isDark ? 'rgba(255, 255, 255, 0.2)' : 'gray.300'}
                borderRadius="lg"
                p={4}
                bg={isDark ? '#1A1C1F' : 'white'}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
              >
                <Text
                  fontWeight="semibold"
                  fontSize="sm"
                  color={isDark ? 'gray.300' : 'gray.500'}
                  mb={2}
                  textAlign="center"
                >
                  Task Progress Monitoring
                </Text>
                <TaskCompletionChart
                  totalTasks={taskStats.total}
                  completedTasks={taskStats.completed}
                  remainingTasks={taskStats.total - taskStats.completed}
                />
              </Box>
            </Flex>
          </Box>
        </>
      ) : (
        selectedChannel && (
          <ChannelDetails
            color={color}
            bg={bg}
            organizationId={organizationId}
            projectId={project.id}
            channelId={selectedChannel.id}
            channelName={selectedChannel.name}
            onBack={() => setSelectedChannel(null)}
            users={users}
            isAdmin={isAdmin}
            currentUser={currentUser}
          />
        )
      )}
    </Box>
  );
};

export default ProjectDetails;